import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Mail, Send, Check, X, Clock, Search, Phone } from "lucide-react";
import { toast, Toaster } from "sonner";

const statusLabel = {
  pending_review: { label: "Zu prüfen", className: "bg-amber-100 text-amber-900" },
  quoted: { label: "Offerte gesendet", className: "bg-blue-100 text-blue-900" },
  confirmed: { label: "Bestätigt", className: "bg-emerald-100 text-emerald-900" },
  declined: { label: "Abgelehnt", className: "bg-gray-200 text-gray-700" },
  expired: { label: "Abgelaufen", className: "bg-red-100 text-red-900" },
};

const RequestsAdminPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState(null);
  const [stats, setStats] = useState([]);

  useEffect(() => {
    if (user && !["admin", "station_manager"].includes(user.role)) {
      navigate("/");
    }
  }, [user, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: r }, { data: s }] = await Promise.all([
        api.get("/booking-requests"),
        api.get("/admin/promo-stats").catch(() => ({ data: { items: [] } })),
      ]);
      setItems(r.items || []);
      setStats(s.items || []);
    } catch (e) {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="rs-app min-h-screen bg-white">
      <Header />
      <Toaster position="top-center" />
      <main className="max-w-[1300px] mx-auto px-6 lg:px-10 py-10">
        <div className="rs-overline">Admin · Anfragen</div>
        <h1 className="font-[Chivo] text-4xl font-black mt-2">Buchungsanfragen</h1>

        {/* Promo Tracking */}
        <section className="mt-10" data-testid="promo-stats-section">
          <div className="rs-overline mb-3">Aktions-Tracking</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {stats.length === 0 ? (
              <div className="col-span-3 text-sm text-[var(--rs-text-muted)] italic">Noch keine Aktivität.</div>
            ) : stats.map((s) => (
              <div key={s.code} className="border-2 border-[var(--rs-border)] p-4" data-testid={`stat-${s.code}`}>
                <div className="rs-overline">{s.code}</div>
                <div className="grid grid-cols-3 gap-2 mt-2 text-center">
                  <div>
                    <div className="text-xl font-[Chivo] font-black">{s.clicks}</div>
                    <div className="text-[10px] uppercase tracking-wider text-[var(--rs-text-muted)]">Klicks</div>
                  </div>
                  <div>
                    <div className="text-xl font-[Chivo] font-black">{s.bookings}</div>
                    <div className="text-[10px] uppercase tracking-wider text-[var(--rs-text-muted)]">Buchungen</div>
                  </div>
                  <div>
                    <div className="text-xl font-[Chivo] font-black text-[var(--rs-primary)]">{s.conversion_pct}%</div>
                    <div className="text-[10px] uppercase tracking-wider text-[var(--rs-text-muted)]">Conv.</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Requests list */}
        <section className="mt-12">
          <div className="rs-overline mb-3">Anfragen ({items.length})</div>
          {loading ? (
            <div className="text-sm text-[var(--rs-text-muted)]">Lade…</div>
          ) : items.length === 0 ? (
            <div className="text-sm text-[var(--rs-text-muted)] italic">Keine Anfragen.</div>
          ) : (
            <div className="border-2 border-[var(--rs-border)]">
              <table className="w-full text-sm" data-testid="requests-table">
                <thead className="bg-[var(--rs-surface)]">
                  <tr>
                    <Th>Nr.</Th><Th>Kunde</Th><Th>Fahrzeug</Th><Th>Beginn</Th><Th>Status</Th><Th></Th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((r) => (
                    <tr key={r.id} className="border-t border-[var(--rs-border)]">
                      <Td><span className="font-mono text-[12px]">{r.request_number}</span></Td>
                      <Td>{r.customer_name || "—"}<br /><span className="text-xs text-[var(--rs-text-muted)]">{r.customer_email}</span></Td>
                      <Td>{r.vehicle_name}<br /><span className="text-xs text-[var(--rs-text-muted)]">{r.vehicle_plate}</span></Td>
                      <Td><span className="text-xs">{r.rental_start}<br />{r.days ? `${r.days} Tage` : `${r.package_hours}h`}</span></Td>
                      <Td><span className={`text-[10px] uppercase tracking-wider px-2 py-1 ${statusLabel[r.status]?.className || ""}`}>{statusLabel[r.status]?.label || r.status}</span></Td>
                      <Td className="text-right">
                        {r.status === "pending_review" || r.status === "quoted" ? (
                          <button
                            type="button"
                            onClick={() => setPicked(r)}
                            className="text-xs uppercase tracking-wider text-[var(--rs-primary)] font-bold underline"
                            data-testid={`open-quote-${r.id}`}
                          >
                            Offerte {r.status === "quoted" ? "anpassen" : "erstellen"} →
                          </button>
                        ) : r.status === "confirmed" && !r.converted_contract_id ? (
                          <ConvertButton request={r} onDone={load} />
                        ) : r.status === "confirmed" && r.converted_contract_id ? (
                          <Link
                            to={`/admin?contract=${r.converted_contract_id}`}
                            className="text-xs uppercase tracking-wider text-[var(--rs-text-muted)] font-bold underline"
                            data-testid={`view-contract-${r.id}`}
                          >
                            Vertrag {r.converted_contract_number} →
                          </Link>
                        ) : (
                          <span className="text-xs text-[var(--rs-text-muted)]">—</span>
                        )}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {picked && (
          <QuoteModal request={picked} onClose={() => { setPicked(null); load(); }} />
        )}
      </main>
      <Footer />
    </div>
  );
};

const Th = ({ children }) => (
  <th className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-[var(--rs-text-muted)] font-bold">{children}</th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-4 py-3 align-top ${className}`}>{children}</td>
);

const ConvertButton = ({ request, onDone }) => {
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const handle = async () => {
    if (!window.confirm(`Vertrag aus Anfrage ${request.request_number} erstellen?\n\nDer Vertrag wird im Status "pending" angelegt. Führerausweis-Nr., ID-Foto und Unterschrift werden bei der Übergabe ergänzt.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/booking-requests/${request.id}/convert-to-contract`);
      const num = data?.contract?.contract_number;
      toast.success(`Vertrag ${num || ""} erstellt`);
      onDone();
      // Soft navigate to admin dashboard so staff can finalize / handover
      setTimeout(() => navigate(`/admin?contract=${data?.contract?.id || ""}`), 600);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Konvertierung fehlgeschlagen";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={handle}
      disabled={busy}
      className="text-xs uppercase tracking-wider text-[var(--rs-primary)] font-bold underline disabled:opacity-50"
      data-testid={`convert-${request.id}`}
    >
      {busy ? "Erstelle…" : "→ Vertrag erstellen"}
    </button>
  );
};

const QuoteModal = ({ request, onClose }) => {
  const q = request.quote || {};
  const [price, setPrice] = useState(q.price_chf || "");
  const [pickup, setPickup] = useState(q.pickup_time || "10:00");
  const [ret, setRet] = useState(q.return_time || "10:00");
  const [notes, setNotes] = useState(q.admin_notes || "");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!price || isNaN(parseFloat(price))) {
      toast.error("Preis fehlt");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/booking-requests/${request.id}/quote`, {
        price_chf: parseFloat(price),
        pickup_time: pickup,
        return_time: ret,
        admin_notes: notes || null,
      });
      toast.success("Offerte gesendet — Kunde wird per E-Mail benachrichtigt");
      onClose();
    } catch (e) {
      toast.error("Senden fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="quote-modal">
      <div className="bg-white border-4 border-[var(--rs-primary)] w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="bg-[var(--rs-primary)] text-white p-5">
          <div className="rs-overline !text-white/60">{request.request_number}</div>
          <h3 className="font-[Chivo] text-2xl font-black mt-1">Offerte erstellen</h3>
          <p className="text-sm text-white/80 mt-1">{request.customer_name} · {request.vehicle_name}</p>
        </div>
        <div className="p-5 space-y-4">
          <div className="bg-[var(--rs-surface)] border border-[var(--rs-border)] p-3 text-xs">
            <div><strong>Beginn:</strong> {request.rental_start}</div>
            {request.rental_end && <div><strong>Ende:</strong> {request.rental_end}</div>}
            <div><strong>Dauer:</strong> {request.days ? `${request.days} Tage` : `${request.package_hours}h`}</div>
            <div><strong>Reiseziel:</strong> {request.destination === "eu" ? "Europa" : request.destination === "ch" ? "Schweiz" : "—"}</div>
            {request.customer_notes && <div className="mt-2"><strong>Notiz Kunde:</strong> {request.customer_notes}</div>}
          </div>
          <label className="block">
            <span className="rs-label">Preis CHF</span>
            <input type="number" min="0" step="10" className="rs-input" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="quote-price-input" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="rs-label">Übergabe-Zeit</span>
              <input type="time" className="rs-input" value={pickup} onChange={(e) => setPickup(e.target.value)} data-testid="quote-pickup-input" />
            </label>
            <label className="block">
              <span className="rs-label">Rückgabe-Zeit</span>
              <input type="time" className="rs-input" value={ret} onChange={(e) => setRet(e.target.value)} data-testid="quote-return-input" />
            </label>
          </div>
          <label className="block">
            <span className="rs-label">Anmerkung an Kunde (optional)</span>
            <textarea className="rs-input min-h-[80px]" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="quote-notes-input" />
          </label>
        </div>
        <div className="p-5 border-t border-[var(--rs-border)] flex justify-between gap-3">
          <button type="button" className="rs-btn-secondary !py-3" onClick={onClose}>Abbrechen</button>
          <button type="button" className="rs-btn-primary !py-3 disabled:opacity-50" onClick={submit} disabled={saving} data-testid="quote-send-btn">
            <Send size={14} className="mr-2" /> {saving ? "Sende…" : "Offerte senden (48h)"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RequestsAdminPage;
